import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '../api/client';
import { uploadFileToS3 } from '../utils/uploadFileToS3';
import { resizeImageForUpload } from '../utils/resizeImageForUpload';

interface UploadResponse {
  upload_url: string;
  media_id: string;
}

interface ImageUrlState {
  url: string;
  expiresAt: number;
}

const parseExpiresAt = (signedUrl: string): number => {
  try {
    const expires = new URL(signedUrl).searchParams.get('Expires');
    if (expires) {
      const parsed = parseInt(expires, 10);
      // Backend returns epoch seconds; convert to ms.
      return parsed > 1e12 ? parsed : parsed * 1000;
    }
  } catch {
    // fall through to default
  }
  return Date.now() + 60 * 60 * 1000;
};

export const useImageHandling = () => {
  const [imageUrls, setImageUrls] = useState<Record<string, ImageUrlState>>({});
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const processedKeysRef = useRef<Set<string>>(new Set());
  const refreshIntervalRef = useRef<NodeJS.Timeout>();
  // Synchronous mirror of imageUrls. React 18's setState updater is not
  // reliably called synchronously from async resolvers (eager evaluation is
  // skipped when the fiber has pending updates), so reading state via the
  // setImageUrls(cb => …) trick is a race. The ref makes reads deterministic.
  const imageUrlsRef = useRef<Record<string, ImageUrlState>>({});
  useEffect(() => {
    imageUrlsRef.current = imageUrls;
  }, [imageUrls]);

  // In-flight de-duplication per id so concurrent resolvers (e.g. multiple
  // image nodes mounted in the same step) share a single network request.
  const inFlightUrlFetchesRef = useRef<Map<string, Promise<ImageUrlState | null>>>(new Map());

  // Consumers (SOPEditor) should call this when sopId/versionId changes —
  // otherwise the dedupe set from a previous SOP blocks getImageUrls forever.
  const resetProcessedKeys = useCallback(() => {
    processedKeysRef.current = new Set();
    inFlightUrlFetchesRef.current = new Map();
  }, []);

  const normalizeImageRefId = useCallback((id: string) => {
    if (!id) return '';
    return id.replace(/^image:\/\//, '');
  }, []);

  const isValidImageRefId = useCallback((id: string) => {
    if (!id) return false;
    if (id.startsWith('http://') || id.startsWith('https://')) {
      return false;
    }
    const uuidPart = normalizeImageRefId(id);
    if (!uuidPart) return false;

    // Accept UUID format
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuidPart)) {
      return true;
    }

    // Accept avatar format
    if (/^avatar-\d+$/i.test(uuidPart)) {
      return true;
    }

    return false;
  }, [normalizeImageRefId]);

  const isUrlExpired = useCallback((urlState: ImageUrlState) => {
    // Add 15 minute buffer before actual expiration (refresh at 45 minutes)
    return Date.now() > urlState.expiresAt - 15 * 60 * 1000;
  }, []);

  // Low-level POST that bypasses processedKeysRef dedupe. Used by getCurrentUrl
  // so a single-image resolver never gets blocked when batch preload already
  // recorded the id. Caches the result back into imageUrlsRef + imageUrls state.
  const fetchUrlsDirect = useCallback(async (
    refIds: string[],
  ): Promise<Record<string, ImageUrlState>> => {
    const normalized = refIds
      .map(normalizeImageRefId)
      .filter(id => id !== '' && isValidImageRefId(id));
    if (normalized.length === 0) return {};

    const res = await api.post('/download_urls', { image_ids: normalized });
    const urls: Record<string, string> = res.data?.urls ?? {};

    const urlsWithExpiry: Record<string, ImageUrlState> = {};
    for (const [id, url] of Object.entries(urls)) {
      urlsWithExpiry[id] = { url, expiresAt: parseExpiresAt(url) };
    }

    if (Object.keys(urlsWithExpiry).length > 0) {
      imageUrlsRef.current = { ...imageUrlsRef.current, ...urlsWithExpiry };
      setImageUrls(prev => ({ ...prev, ...urlsWithExpiry }));
    }

    return urlsWithExpiry;
  }, [isValidImageRefId, normalizeImageRefId]);

  const getImageUrls = useCallback(async (refIds: string[]) => {
    if (refIds.length === 0) return {};

    const newRefIds = refIds
      .map(normalizeImageRefId)
      .filter(id =>
        id !== '' &&
        isValidImageRefId(id) &&
        !processedKeysRef.current.has(id)
      );

    if (newRefIds.length === 0) return {};

    newRefIds.forEach(id => processedKeysRef.current.add(id));

    try {
      const urlsWithExpiry = await fetchUrlsDirect(newRefIds);
      return urlsWithExpiry;
    } catch (error) {
      console.error('Error fetching image URLs:', error);
      newRefIds.forEach(id => processedKeysRef.current.delete(id));
      throw error;
    }
  }, [fetchUrlsDirect, isValidImageRefId, normalizeImageRefId]);

  // Helper function to validate and refresh URLs if needed
  const validateAndRefreshUrls = useCallback(async (urls: Record<string, ImageUrlState>) => {
    const expiredIds = Object.entries(urls)
      .filter(([, urlState]) => isUrlExpired(urlState))
      .map(([id]) => id);

    if (expiredIds.length > 0) {
      try {
        // Bypass processedKeysRef so expired ids actually refresh.
        const newUrls = await fetchUrlsDirect(expiredIds);
        return newUrls;
      } catch (error) {
        console.error('Error refreshing expired URLs:', error);
        return {};
      }
    }
    return urls;
  }, [fetchUrlsDirect, isUrlExpired]);

  const refreshExpiredUrls = useCallback(async () => {
    const currentUrls = imageUrlsRef.current;
    const expiredIds = Object.entries(currentUrls)
      .filter(([, urlState]) => isUrlExpired(urlState))
      .map(([id]) => id);

    if (expiredIds.length === 0) return;

    try {
      await fetchUrlsDirect(expiredIds);
    } catch (error) {
      console.error('Error refreshing expired URLs:', error);
    }
  }, [fetchUrlsDirect, isUrlExpired]);

  // Set up periodic refresh of expired URLs
  useEffect(() => {
    // Check for expired URLs every minute
    refreshIntervalRef.current = setInterval(refreshExpiredUrls, 60 * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [refreshExpiredUrls]);

  const handleFileUpload = useCallback(async (file: File, isLocal?: boolean) => {
    try {
      if (isLocal) {
        const imageRefId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const downloadUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
        return { imageRefId, downloadUrl };
      }

      if (file.type.startsWith('image/')) {
        file = await resizeImageForUpload(file);
      }

      const response = await uploadFileToS3(file);
      if (!response || typeof response === 'string') {
        throw new Error("Failed to upload image");
      }

      if (!isUploadResponse(response)) {
        throw new Error("Invalid upload response format");
      }

      const urls = await getImageUrls([response.media_id]);
      const urlState = urls[response.media_id];
      if (!urlState) throw new Error("Failed to get download URL");

      return { imageRefId: response.media_id, downloadUrl: urlState.url };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }, [getImageUrls]);

  // Resolve a single image id to a presigned URL.
  //
  // Reads imageUrlsRef synchronously (no setState hack), and on miss fetches
  // directly via /download_urls — bypassing the processedKeysRef dedupe that
  // `getImageUrls` uses for batch preloads. Concurrent calls for the same id
  // share a single in-flight request.
  const getCurrentUrl = useCallback(async (imageRefId: string): Promise<string | null> => {
    if (!imageRefId) return null;
    const normalized = normalizeImageRefId(imageRefId);
    const key = normalized.toLowerCase();

    const cached =
      imageUrlsRef.current[normalized] ??
      imageUrlsRef.current[key] ??
      imageUrlsRef.current[imageRefId];
    if (cached && !isUrlExpired(cached)) return cached.url;

    const inFlight = inFlightUrlFetchesRef.current.get(normalized);
    if (inFlight) {
      const state = await inFlight;
      return state?.url ?? null;
    }

    const pending = (async (): Promise<ImageUrlState | null> => {
      try {
        const newUrls = await fetchUrlsDirect([normalized]);
        return newUrls[normalized] ?? newUrls[key] ?? null;
      } catch (error) {
        console.error('Error fetching URL directly:', error);
        return null;
      } finally {
        inFlightUrlFetchesRef.current.delete(normalized);
      }
    })();
    inFlightUrlFetchesRef.current.set(normalized, pending);

    const state = await pending;
    return state?.url ?? null;
  }, [fetchUrlsDirect, isUrlExpired, normalizeImageRefId]);

  return {
    imageUrls,
    setImageUrls,
    isLoadingImages,
    setIsLoadingImages,
    handleFileUpload,
    getImageUrls,
    isValidImageRefId,
    normalizeImageRefId,
    getCurrentUrl,
    validateAndRefreshUrls,
    resetProcessedKeys,
  };
};

function isUploadResponse(value: unknown): value is UploadResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'upload_url' in value &&
    'media_id' in value &&
    typeof (value as UploadResponse).upload_url === 'string' &&
    typeof (value as UploadResponse).media_id === 'string'
  );
}
